/**
 * 確認①: AI が作ったスポット一覧の確認
 *
 * ここで外した分には場所検索が 1 回も走らない。名前を直してから検索させることで解決精度も上がる。
 */

import { Check, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

import type { ConfirmSpotParams, ImportCandidate } from '@/api/types/stamp-rally'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

import { ImportSearchAffixFields } from './import-search-affix-fields'

type EditableSpot = {
  name: string
  groupName: string
  orderIndex: number | null
  aiHint: string | null
  checked: boolean
}

type Mode = 'list' | 'text'

const toEditable = (candidates: ImportCandidate[]): EditableSpot[] =>
  candidates.map((candidate) => ({
    name: candidate.name,
    groupName: candidate.group_name ?? '',
    orderIndex: candidate.order_index,
    aiHint: candidate.ai_hint,
    checked: candidate.approval !== 'excluded',
  }))

/** 「都道府県,名前」の 1 行 1 スポット。区切りが無ければ全部を名前として扱う */
const parseTextLines = (text: string): EditableSpot[] =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const separator = line.indexOf(',')
      if (separator < 0) {
        return { name: line, groupName: '', orderIndex: null, aiHint: null, checked: true }
      }
      return {
        name: line.slice(separator + 1).trim(),
        groupName: line.slice(0, separator).trim(),
        orderIndex: null,
        aiHint: null,
        checked: true,
      }
    })
    .filter((spot) => spot.name.length > 0)

const toText = (spots: EditableSpot[]): string =>
  spots.map((spot) => (spot.groupName ? `${spot.groupName},${spot.name}` : spot.name)).join('\n')

type Props = {
  candidates: ImportCandidate[]
  isSubmitting: boolean
  initialPrefix: string
  initialSuffix: string
  onConfirm: (spots: ConfirmSpotParams[], searchPrefix: string, searchSuffix: string) => void
}

export const ImportSpotReview = ({ candidates, isSubmitting, initialPrefix, initialSuffix, onConfirm }: Props) => {
  const [spots, setSpots] = useState<EditableSpot[]>(() => toEditable(candidates))
  const [mode, setMode] = useState<Mode>('list')
  const [draftText, setDraftText] = useState('')
  const [searchPrefix, setSearchPrefix] = useState(initialPrefix)
  const [searchSuffix, setSearchSuffix] = useState(initialSuffix)

  const checkedCount = spots.filter((spot) => spot.checked).length

  const updateSpot = (index: number, patch: Partial<EditableSpot>) => {
    setSpots((prev) => prev.map((spot, i) => (i === index ? { ...spot, ...patch } : spot)))
  }

  const handleChangeMode = (next: Mode) => {
    if (next === 'text') setDraftText(toText(spots))
    if (next === 'list' && draftText) setSpots(parseTextLines(draftText))
    setMode(next)
  }

  const handleSubmit = () => {
    const source = mode === 'text' ? parseTextLines(draftText) : spots
    onConfirm(
      source.map((spot) => ({
        name: spot.name,
        group_name: spot.groupName || null,
        order_index: spot.orderIndex,
        ai_hint: spot.aiHint,
        approval: spot.checked ? 'approved' : 'excluded',
      })),
      searchPrefix.trim(),
      searchSuffix.trim(),
    )
  }

  const submitCount = mode === 'text' ? parseTextLines(draftText).length : checkedCount

  return (
    <div className="flex h-full flex-col">
      <div className="border-b px-4 py-3">
        <p className="text-sm">
          <span className="font-semibold tabular-nums">{submitCount}</span> 件を検索します
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          存在しないスポットが混じっていることがあります。外した分は検索しません。
        </p>
      </div>

      <Tabs value={mode} onValueChange={(next) => handleChangeMode(next as Mode)} className="px-3 pt-3">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="list">1 件ずつ</TabsTrigger>
          <TabsTrigger value="text">まとめて編集</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className={cn('min-h-0 flex-1 overflow-y-auto p-3', mode !== 'list' && 'hidden')}>
        <ul className="space-y-2">
          {spots.map((spot, index) => (
            <li
              key={index}
              className={cn('flex items-center gap-2 rounded-xl border bg-card p-2', !spot.checked && 'opacity-50')}
            >
              <Checkbox
                checked={spot.checked}
                onCheckedChange={(checked) => updateSpot(index, { checked: checked === true })}
              />
              <Input
                value={spot.groupName}
                onChange={(e) => updateSpot(index, { groupName: e.target.value })}
                placeholder="都道府県"
                className="w-28 shrink-0"
              />
              <Input
                value={spot.name}
                onChange={(e) => updateSpot(index, { name: e.target.value })}
                placeholder="スポット名"
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label="この行を削除"
                onClick={() => setSpots((prev) => prev.filter((_, i) => i !== index))}
              >
                <Trash2 className="size-4" />
              </Button>
            </li>
          ))}
        </ul>

        <Button
          variant="outline"
          className="mt-3 w-full"
          onClick={() =>
            setSpots((prev) => [...prev, { name: '', groupName: '', orderIndex: null, aiHint: null, checked: true }])
          }
        >
          <Plus className="mr-2 size-4" />
          スポットを追加
        </Button>
      </div>

      <div className={cn('min-h-0 flex-1 overflow-y-auto p-3', mode !== 'text' && 'hidden')}>
        <p className="mb-2 text-xs text-muted-foreground">1 行 1 スポット。「都道府県,名前」の形式です。</p>
        <Textarea
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
          rows={20}
          className="font-mono text-sm"
        />
      </div>

      <div className="border-t p-3">
        <ImportSearchAffixFields
          prefix={searchPrefix}
          suffix={searchSuffix}
          onPrefixChange={setSearchPrefix}
          onSuffixChange={setSearchSuffix}
          idPrefix="import-review"
        />
        <Button className="mt-3 w-full" disabled={submitCount === 0 || isSubmitting} onClick={handleSubmit}>
          <Check className="mr-2 size-4" />
          {submitCount} 件の場所を検索する
        </Button>
      </div>
    </div>
  )
}
